import { configureApiClient, installInterceptors } from '@cenorion/api';

import { env } from '@/core/env/env';

import { subscribeToAuthMessages } from './auth-broadcast';
import { leaveProtectedUi } from './leave-protected-ui';
import { refreshAndCacheToken } from './oidc-session';
import { resyncPermissions } from './permissions-from-identity';
import { endSession } from './session-manager';
import { getBearerToken, setBearerToken, useSessionStore } from './session-store';

/**
 * The interceptor's refresh, followed by a re-read of the caller's grants so a role change lands at
 * the next token refresh. Fire-and-forget: the refreshed token is returned at once, so the 401
 * replay never waits on the identity reads. Boot's own stored-token refresh does not come through
 * here — boot resolves the grants itself during profile adoption.
 */
async function refreshAndThenResync(): Promise<string> {
  const token = await refreshAndCacheToken();
  void resyncPermissions();
  return token;
}

/**
 * Connects this app's session to the shared API client: `packages/api` owns where refresh runs in
 * the interceptor chain, this module supplies what it does — a single-flight silent OIDC refresh.
 * Navigation is injected the same way in reverse, so the auth layer never imports the router.
 */
export function installAuth(): void {
  configureApiClient({
    baseURL: env.VITE_API_BASE_URL,
    getAccessToken: getBearerToken,
    refresh: refreshAndThenResync,
    onAuthFailure: endSession,
  });
  installInterceptors();

  // Cross-tab logout propagation
  subscribeToAuthMessages((message) => {
    if (message.type === 'logout') {
      setBearerToken(null);
      useSessionStore.getState().setAnonymous();
      void leaveProtectedUi();
    }
  });
}
